import WarningIcon from '@mui/icons-material/Warning';
import { useTheme } from '@mui/material/styles';
import { ICellRendererParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { DeliveryProduct } from '../../store/apiSlices/delivery/deliveryApiParser';
import Box from '@mui/material/Box';
import Button from '../common/Button';
import Card from '../common/Card';
import ClientSideAgGrid from '../common/customAgGrid/ClientSideAgGrid';
import EditableField from '../common/EditableField';
import Typography from '@mui/material/Typography';
import { SectionHeading } from '../common/styled/SectionHeading';
import { useTranslation } from 'react-i18next';

// Type definitions
interface ProductsTableProps {
  onSave: any;
  submitting?: boolean;
  products?: DeliveryProduct[];
  onProductChange: (_productId: number, _field: keyof DeliveryProduct, _value: string) => void;
  isLoading?: boolean;
  onOpenLubricationChart: () => void;
  onCancel?: () => void;
  deliveryType?: string;
}

interface EditableCellProps {
  field: keyof DeliveryProduct;
  placeholder: string;
  suffix?: string;
  disabled?: boolean;
  onValueChange?: (_id: number, _field: keyof DeliveryProduct, _value: string) => void;
}

const EditableCellRenderer = React.memo((params: ICellRendererParams & EditableCellProps) => {
  const { data, field, placeholder, suffix, disabled, onValueChange } = params;

  const [localValue, setLocalValue] = React.useState<string>(String(data?.[field] || ''));
  const lastCommitted = React.useRef<string>(String(data?.[field] || ''));
  const debounceTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const newVal = String(data?.[field] || '');
    setLocalValue(newVal);
    lastCommitted.current = newVal;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync from data only
  }, [data, data?.id, data?.[field]]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const regex = /^[0-9]*[.]?[0-9]{0,2}$/;
    if (!regex.test(val)) return;

    setLocalValue(val);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = window.setTimeout(() => {
      onValueChange?.(data.id, field, val);
    }, 500);
  };

  const handleBlur = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (localValue !== lastCommitted.current) {
      lastCommitted.current = localValue;
      onValueChange?.(data.id, field, localValue); // commit on blur
    }
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <EditableField
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      suffix={suffix}
      disabled={disabled}
      type="number"
      inputProps={{
        inputMode: 'decimal',
        pattern: '[0-9]*[.]?[0-9]{0,2}',
      }}
    />
  );
});

EditableCellRenderer.displayName = 'EditableCellRenderer';

const ProductsTable: React.FC<ProductsTableProps> = ({
  onSave,
  submitting = false,
  products,
  onProductChange,
  isLoading = false,
  onOpenLubricationChart,
  onCancel,
  deliveryType,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const gridRef = useRef<AgGridReact>(null);
  const productRef = useRef<DeliveryProduct[]>(products);
  const gridContainerRef = useRef<HTMLDivElement>(null);

  const getDynamicHeight = useCallback(() => {
    // Compute container height from row count with sensible bounds
    const count = products?.length ?? 0;
    const HEADER_HEIGHT = 50;
    const ROW_HEIGHT = 45;
    const MIN_HEIGHT = 100;
    const MAX_HEIGHT = 600;

    const computed = HEADER_HEIGHT + count * ROW_HEIGHT;
    const clamped = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, computed));
    return `${clamped}px`;
  }, [products?.length]);

  useEffect(() => {
    productRef.current = products;
    getDynamicHeight();
  }, [products, getDynamicHeight]);

  const handleProductValueChange = useCallback(
    (productId: number, field: keyof DeliveryProduct, value: string) => {
      // Never allow editing of the immutable id field
      if (field === 'id') return;

      // Notify parent of the change so it persists across refetches
      onProductChange(productId, field, value);

      // update ref values
      productRef.current = productRef?.current?.map((p) =>
        p.id === productId ? { ...p, [field]: value } : p,
      );

      // update grid row immutably (data objects may be frozen)
      const rowNode = gridRef.current?.api.getRowNode(productId.toString());
      if (rowNode) {
        const current = rowNode.data || {};
        const newData = { ...current, [field]: value };
        // keep id stable for getRowId
        if ((current as any).id != null && (newData as any).id == null) {
          (newData as any).id = (current as any).id;
        }
        rowNode.setData(newData);
      }
    },
    [onProductChange],
  );

  // Type-in indicator cell renderer (for dedicated column)
  const TypeInIndicatorCellRenderer = useCallback(
    (params: ICellRendererParams) => {
      if (!params.data.isTypeIn) {
        return null;
      }
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            height: '100%',
            justifyContent: 'center',
          }}
        >
          <WarningIcon sx={{ color: theme.palette.warningAmber, fontSize: 20 }} />
          <Typography
            sx={{
              fontSize: '12px',
              color: theme.palette.warningAmber,
              fontWeight: 500,
            }}
          >
            {t('table.columns.typeIn')}
          </Typography>
        </Box>
      );
    },
    [t, theme],
  );

  // Optimized column definitions
  const columnDefs: any = useMemo(() => {
    let cols: any[] = [];

    if (deliveryType === 'adjusting') {
      // Adjusting delivery columns
      cols = [
        {
          headerName: t('table.columns.productName'),
          field: 'name',
          width: 130,
          floatingFilter: false,
          filter: false,
        },
        {
          headerName: t('table.columns.maker'),
          field: 'maker',
          width: 120,
          floatingFilter: false,
          filter: false,
        },
        {
          headerName: t('table.columns.category'),
          field: 'category',
          width: 150,
          floatingFilter: false,
          filter: false,
        },
        {
          headerName: t('table.columns.type'),
          field: 'type',
          width: 150,
          floatingFilter: false,
          filter: false,
        },
        {
          headerName: t('table.columns.storageQuantity'),
          field: 'storageQuantity',
          width: 150,
          floatingFilter: false,
          filter: false,
          cellRenderer: (params: any) => (
            <EditableCellRenderer
              {...params}
              field="storageQuantity"
              placeholder={t('form.quantityPlaceholder')}
              suffix={params?.data?.unit}
              onValueChange={handleProductValueChange}
            />
          ),
          suppressKeyboardEvent: () => true,
        },
        {
          headerName: t('table.columns.sumpTankQuantity'),
          field: 'sumpTankQuantity',
          width: 180,
          floatingFilter: false,
          filter: false,
          cellRenderer: (params: any) => (
            <EditableCellRenderer
              {...params}
              field="sumpTankQuantity"
              placeholder={t('form.quantityPlaceholder')}
              suffix={params?.data?.unit}
              onValueChange={handleProductValueChange}
              disabled={!params?.data?.isSumpTankMandatory}
            />
          ),
          suppressKeyboardEvent: () => true,
        },
      ];
    } else if (deliveryType === 'secondHand') {
      // 2nd Hand Vessel Acquisition delivery columns
      cols = [
        {
          headerName: t('lubricants.products'),
          field: 'name',
          width: 130,
          floatingFilter: false,
          filter: false,
        },
        {
          headerName: t('table.columns.maker'),
          field: 'maker',
          width: 100,
          floatingFilter: false,
          filter: false,
        },
        {
          headerName: t('table.columns.category'),
          field: 'category',
          width: 130,
          floatingFilter: false,
          filter: false,
        },
        {
          headerName: t('table.columns.type'),
          field: 'type',
          width: 100,
          floatingFilter: false,
          filter: false,
        },
        {
          headerName: t('table.columns.storageQuantity'),
          field: 'storageQuantity',
          width: 150,
          floatingFilter: false,
          filter: false,
          cellRenderer: (params: any) => (
            <EditableCellRenderer
              {...params}
              field="storageQuantity"
              placeholder={t('form.quantityPlaceholder')}
              suffix={params?.data?.unit}
              onValueChange={handleProductValueChange}
            />
          ),
          suppressKeyboardEvent: () => true,
        },
        {
          headerName: t('table.columns.sumpTankQuantity'),
          field: 'sumpTankQuantity',
          width: 180,
          floatingFilter: false,
          filter: false,
          cellRenderer: (params: any) => (
            <EditableCellRenderer
              {...params}
              field="sumpTankQuantity"
              placeholder={t('form.quantityPlaceholder')}
              suffix={params?.data?.unit}
              onValueChange={handleProductValueChange}
              disabled={!params?.data?.isSumpTankMandatory}
            />
          ),
          suppressKeyboardEvent: () => true,
        },
        {
          headerName: t('table.columns.netValue'),
          field: 'netValue',
          width: 150,
          floatingFilter: false,
          filter: false,
          cellRenderer: (params: any) => (
            <EditableCellRenderer
              {...params}
              field="netValue"
              placeholder={t('form.valuePlaceholder')}
              suffix={params?.data?.netValueUnit}
              onValueChange={handleProductValueChange}
            />
          ),
          suppressKeyboardEvent: () => true,
        },
      ];
    } else {
      // Standard/New Building delivery columns
      cols = [
        {
          headerName: t('table.columns.productName'),
          field: 'name',
          width: 130,
          floatingFilter: false,
          filter: false,
        },
        {
          headerName: t('table.columns.maker'),
          field: 'maker',
          width: 100,
          floatingFilter: false,
          filter: false,
        },
        {
          headerName: t('table.columns.category'),
          field: 'category',
          width: 130,
          floatingFilter: false,
          filter: false,
        },
        {
          headerName: t('table.columns.type'),
          field: 'type',
          width: 100,
          floatingFilter: false,
          filter: false,
        },
        {
          headerName: t('table.columns.quantity'),
          field: 'quantity',
          width: 150,
          floatingFilter: false,
          filter: false,
          cellRenderer: (params: any) => (
            <EditableCellRenderer
              {...params}
              field="quantity"
              placeholder={t('form.quantityPlaceholder')}
              suffix={params?.data?.quantityUnit}
              onValueChange={handleProductValueChange}
            />
          ),
          suppressKeyboardEvent: () => true,
        },
        {
          headerName: t('table.columns.netValue'),
          field: 'netValue',
          width: 150,
          floatingFilter: false,
          filter: false,
          cellRenderer: (params: any) => (
            <EditableCellRenderer
              {...params}
              field="netValue"
              placeholder={t('form.valuePlaceholder')}
              suffix={params?.data?.netValueUnit}
              onValueChange={handleProductValueChange}
            />
          ),
          suppressKeyboardEvent: () => true,
        },
        {
          headerName: t('table.columns.packagingCost'),
          field: 'packagingCost',
          width: 150,
          floatingFilter: false,
          filter: false,
          cellRenderer: (params: any) => (
            <EditableCellRenderer
              {...params}
              field="packagingCost"
              placeholder={t('form.costPlaceholder')}
              suffix={params?.data?.packagingCostUnit}
              onValueChange={handleProductValueChange}
            />
          ),
          suppressKeyboardEvent: () => true,
        },
      ];
    }

    // Conditionally add isTypeIn column if any product has isTypeIn true
    const hasTypeIn = products?.some((p) => p.isTypeIn === true);
    if (hasTypeIn) {
      cols.push({
        headerName: '',
        field: 'isTypeIn',
        width: 100,
        floatingFilter: false,
        filter: false,
        sortable: false,
        cellRenderer: TypeInIndicatorCellRenderer,
      });
    }

    return cols;
  }, [deliveryType, products, handleProductValueChange, TypeInIndicatorCellRenderer, t]);

  return (
    <Box sx={{ width: '100%', marginBottom: 3 }}>
      {/* Products Table */}
      <Card sx={{ mt: 2, padding: '24px' }}>
        <Box sx={{ mb: 1.5, mt: -1.5 }}>
          <SectionHeading>
            {t('deliveries.productsAvailableForDelivery', { count: products?.length })}
          </SectionHeading>
        </Box>

        {/* Custom divider that spans full width */}
        <Box
          sx={{
            height: '2px',
            backgroundColor: theme.palette.dropdown?.border,
            mx: '-24px', // Negative margin to override card padding
            mb: 3,
          }}
        />

        <Box
          ref={gridContainerRef}
          className="ag-theme-alpine"
          sx={{
            position: 'relative', // Ensure tooltips are positioned relative to this container
          }}
        >
          <ClientSideAgGrid
            ref={gridRef}
            rowData={products}
            columnDefs={columnDefs}
            loading={isLoading}
            className="products-table-grid"
            themeParams={{
              rowBorder: false,
              headerRowBorder: false,
              headerBackgroundColor: theme.palette.datagridHeaderAlt,
            }}
            infiniteRows={true}
            getRowId={(params: any) => params.data.id?.toString?.()}
            domLayout="autoHeight"
          />
        </Box>

        {/* Bottom text */}
        <Box mt={3}>
          <Typography
            sx={{
              color: theme.palette.text?.secondary,
              fontSize: '15px',
              fontWeight: 500,
            }}
          >
            {t('deliveries.productNotListed')}{' '}
            <Box
              component="span"
              sx={{
                color: theme.palette.primary?.main,
                cursor: 'pointer',
              }}
              onClick={onOpenLubricationChart}
            >
              {t('deliveries.checkLubChart')}
            </Box>
          </Typography>
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
          <Button variant="red" onClick={onCancel}>
            {t('buttons.cancel')}
          </Button>
          <Button
            variant="outlinedBlue"
            onClick={() => onSave(productRef?.current)}
            isLoading={submitting}
          >
            {t('deliveries.saveDelivery')}
          </Button>
        </Box>
      </Card>
    </Box>
  );
};

export default ProductsTable;
